import type { AxiosError } from "axios";
import type { IClient, ServerErrorResponse } from "src/types/global";
import { useEffect, useCallback, useState, useContext } from "react";
import EditClientUI from "./EditClientUI";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { clientService } from "@services/ClientService";
import { GlobalAlertContext } from "@providers/GlobalAlertProvider";
import { useLocation } from "react-router-dom";
const EditClient = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    setValue,
    formState: {
      errors
    }
  } = useForm<IClient>({});
  const [client, setClient] = useState<IClient>({
    id: -1,
    fullName: "",
    identification: "N/A",
    phone: "",
    email: "",
    creationTime: "",
    moneyCapAmount: undefined
  });
  const [loadingData, setLoadingData] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("Lo sentimos, no se pudo actualizar este cliente, inténtalo nuevamente");
  const [showError, setShowError] = useState(false);
  const {
    setGlobalAlertMessage,
    setGlobalAlertSeverity,
    showGlobalAlert
  } = useContext(GlobalAlertContext);
  const onSubmit = async (newClient: IClient) => {
    try {
      setGlobalAlertSeverity("success");
      setGlobalAlertMessage("!Listo! El cliente ha sido actualizado correctamente");
      setIsLoading(true);
      const payload = {
        ...client,
        ...newClient
      };
      const res = await clientService.put(payload);
      navigate(`/clientes/${res.data.id}`, {
        state: {
          showAlert: true,
          client: {
            ...res.data
          }
        }
      });
      showGlobalAlert();
    } catch (e) {
      const error = (e as AxiosError<ServerErrorResponse>);
      if (error.response && error.response.data.error.message.includes("is already taken")) {
        setErrorMessage("Ya existe un cliente con esta identificación");
      } else {
        setErrorMessage("Lo sentimos, no se pudo actualizar este cliente, inténtalo nuevamente");
      }
      setShowError(true);
    } finally {
      setIsLoading(false);
    }
  };
  const getClient = useCallback(async (id: number, withState: boolean = false) => {
    try {
      if (!withState) {
        setLoadingData(true);
      }
      const res = await clientService.get(id);
      const clientDB = res.data;
      setClient(clientDB);
      setValue("fullName", clientDB.fullName);
      setValue("identification", clientDB.identification);
      setValue("phone", clientDB.phone);
      setValue("email", clientDB.email);
      if (clientDB.moneyCapAmount != null) {
        setValue("moneyCapAmount", clientDB.moneyCapAmount);
      }
    } catch (error) {
      setGlobalAlertSeverity("error");
      setGlobalAlertMessage("El cliente no existe");
      navigate("/clientes", {
        state: {
          showAlert: true
        }
      });
      setTimeout(() => {
        showGlobalAlert();
      }, 300);
    } finally {
      setLoadingData(false);
    }
  }, [navigate, setGlobalAlertSeverity, setGlobalAlertMessage, showGlobalAlert, setValue]);
  useEffect(() => {
    const id = Number(location.pathname.split("/").reverse()[0]);
    const routeState: IClient = location.state;
    if (id === routeState?.id) {
      setClient(routeState);
      setValue("fullName", routeState.fullName);
      setValue("identification", routeState.identification);
      setValue("phone", routeState.phone);
      setValue("email", routeState.email);
      if (routeState.moneyCapAmount != null) {
        setValue("moneyCapAmount", routeState.moneyCapAmount);
      }
      return;
    }
    getClient(id, false);
  }, [getClient, location, setValue]);
  return <EditClientUI register={register} onSubmit={handleSubmit(onSubmit)} loadingData={loadingData} isLoading={isLoading} errorMessage={errorMessage} showError={showError} errors={errors} closeError={() => setShowError(false)} handleCancelClick={() => navigate("/clientes")} />;
};
export default EditClient;